import Reflux from 'reflux';


import i18n from '../configs/i18n';
import appState from '../appState';

import { renderModaltoBody, alert } from '../components/common/Modal';

import RESOURCE_TYPES from '../configs/resourceTypes';
import PRIVILEGE_CODES from '../configs/privilegeCodes';
import { resolveViewType, VIEW_TYPES } from '../utils/viewType';
import { checkAccessOnObject } from '../utils/rights';

import ViewInputModal from '../components/views/ViewInputModal';
import SectionEditModal from '../components/App/AppBody/Section/SectionEditModal';

import modalsActions from '../actions/modalsActions';
import viewsActions from '../actions/viewActions';
import apiActions from '../actions/apiActions';

const ModalStore = Reflux.createStore({
  listenables: [modalsActions],

  openSectionCreateModal(resolveFn) {
    renderModaltoBody(SectionEditModal, {
      isNew: true,
      onOk: (params) => {
        const data = {
          name: params.name,
          icon: 'content-3',
        };

        if (params.id) {
          data.id = params.id
        }

        apiActions.createSection({}, data)
          .then((res) => {
            // access
            const sectionId = res.id;
            switch (params.rights) {
              case 'all':
                // all is done
                break;
              case 'me':
                // change rights
                const data = {
                  object: { sectionId },
                  rules: [{
                    privilegeCode: PRIVILEGE_CODES.ADMIN,
                    rightSubject: {
                      catalogId: '$users',
                      recordId: appState.getIn(['user', 'id']),
                      userAttr: 'id'
                    }
                  }],
                };
                apiActions.createRight({}, data)
                  .then(() => { })
                  .catch((e) => {
                    alert({
                      headerText: i18n.t('message.saveError'),
                      text: i18n.t('modals.saveError.text'),
                      okText: i18n.t('modals.saveError.okText'),
                    });
                  });
                break;
              case 'set':
                // show popup
                const object = { sectionId };
                modalsActions.openAccessModal({ object }, RESOURCE_TYPES.SECTION, { isAdmin: true }, true);
                break;
            }

            // callback
            resolveFn && resolveFn(res);
          });
      }
    });
  },
  openSectionEditModal(sectionId, resolveFn) {
    const section = appState.getIn(['sections', sectionId]);

    renderModaltoBody(SectionEditModal, {
      isNew: false,
      id: sectionId,
      name: section && section.get('name'),
      onOk: (params) => {
        const data = {
          id: params.id || null, // null to delete text id
          name: params.name,
        };

        apiActions.updateSection({ sectionId }, data)
          .then((res) => {
            // callback
            resolveFn && resolveFn(res);
          });
      }
    });
  },

  // eslint-disable-next-line default-param-last
  openAccessModal({ object, parents }, resource, accessProps, hasAdminRule = false, onCloseCb) {
    const RightsModal = require('../components/access/AccessModalController').default;
    const { renderModaltoBody } = require('../components/common/Modal');
    const { readOnly, isAdmin } = accessProps;
    renderModaltoBody(RightsModal, {
      object,
      parents,
      resource,
      readOnly,
      isAdmin,
      hasAdminRule,
      onOk: onCloseCb,
    });
  },

  openViewFieldRightsModal(rule, index, object, subjectPrivilege, basePrivilege, catalogId, fields, onOk, onCancel) {
    const FieldRightsModal = require('../components/access/FieldRightsModal').default;
    const { renderModaltoBody } = require('../components/common/Modal');
    renderModaltoBody(FieldRightsModal, {
      rule,
      index,
      object,
      subjectPrivilege,
      basePrivilege,
      catalogId,
      fields,
      onOk,
      onCancel,
    });
  },

  openViewAccessModal(view, readOnly, onCloseCb) {
    const catalogId = view.get('catalogId');
    const object = {
      viewId: view.get('id'),
    };

    const parents = [
      {
        sectionId: appState.getIn(['catalogs', catalogId, 'sectionId']),
      },
      {
        catalogId,
      },
    ];

    this.openAccessModal({ object, parents }, RESOURCE_TYPES.VIEW, { readOnly }, false, onCloseCb);
  },

  /**
   * Open modal create related record
   * @param catalogId
   * @param linkedRecord {Object<catalog, record>} record to link with
   * @param options {Object}
   */

  openViewInputModal(sceneId, viewId, accessOnViewForRights, resolveFn, viewMode) {
    const { renderModaltoBody } = require('../components/common/Modal');
    const catalogId = appState.getIn(['scenes', sceneId, 'params', 'catalogId']);
    const catalog = appState.getIn(['catalogs', String(catalogId)]) || appState.getIn(['catalogs', catalogId]);
    const canChangeType = !!accessOnViewForRights;
    renderModaltoBody(ViewInputModal, {
      isNew: true,
      headerText: i18n.t('modals.createNewView.headerText'),
      onOk: (params) =>
        viewsActions.createNewView(sceneId, viewId, params, (res) =>
          resolveFn({ ...res, viewMode: params.viewMode, type: params.type }),
        ),
      disabledChangeType: !canChangeType,
      viewMode,
    });
  },

  openViewInputModalEdit(currView, catalogId, sceneId, onSave) {
    const { renderModaltoBody } = require('../components/common/Modal');
    const catalog = appState.getIn(['catalogs', String(catalogId)]) || appState.getIn(['catalogs', catalogId]);
    const canChangeType = checkAccessOnObject(RESOURCE_TYPES.CATALOG, catalog, PRIVILEGE_CODES.ACCESS);
    renderModaltoBody(ViewInputModal, {
      isNew: false,
      headerText: i18n.t('modals.editNewView.headerText'),
      name: currView.get('name'),
      originName: currView.get('originName'),
      rights: currView.get('forRights'),
      type: resolveViewType(currView),
      viewMode: currView.get('viewMode'),
      viewId: currView.get('id'),
      disabledChangeType: !canChangeType,
      onOk: async (params) => {
        const { viewId: newId, ...rest } = params;
        const normalizedNewId = newId || null;
        const idChanged = String(normalizedNewId ?? '') !== String(currView.get('id'));
        const rightsChanged = rest.forRights !== currView.get('forRights');
        const typeChanged = resolveViewType(currView) !== rest.type;

        let result;
        let resolvedViewId;

        if (idChanged && (rightsChanged || typeChanged)) {
          const updateIdResult = await apiActions.updateView(
            { catalogId, viewId: currView.get('id'), forRights: currView.get('forRights') },
            {
              id: normalizedNewId,
              name: rest.name,
              originName: rest.originName,
              viewMode: rest.viewMode,
              type: resolveViewType(currView),
              forRights: Boolean(currView.get('forRights')),
            },
            { sceneId },
          );
          resolvedViewId = updateIdResult && updateIdResult.id;
          result = await apiActions.updateView(
            { catalogId, viewId: resolvedViewId ?? normalizedNewId, forRights: currView.get('forRights') },
            { ...rest },
            { sceneId },
          );
        } else {
          const body = { ...rest };
          if (idChanged) body.id = normalizedNewId;
          result = await apiActions.updateView(
            { catalogId, viewId: currView.get('id'), forRights: currView.get('forRights') },
            body,
            { sceneId },
          );
        }

        if (result && Object.prototype.hasOwnProperty.call(result, 'id')) {
          resolvedViewId = result.id;
        }
        if (resolvedViewId === undefined && normalizedNewId !== null) {
          resolvedViewId = normalizedNewId;
        }
        if (resolvedViewId !== undefined) {
          result = { ...(result || {}), id: resolvedViewId };
        }

        onSave && onSave(params, result);

        const rightsChangedToForRights =
          resolveViewType(currView) !== VIEW_TYPES.SHARED && rest.type === VIEW_TYPES.SHARED;
        if (rightsChangedToForRights) {
          const viewId = idChanged ? newId : currView.get('id');
          const view =
            appState.getIn(['catalogs', catalogId, 'views', viewId]) ||
            appState.getIn(['catalogs', String(catalogId), 'views', String(viewId)]);
          if (view) {
            modalsActions.openViewAccessModal(view, false, () => { });
          }
        }
      },
    });
  },
});

export default ModalStore;
